import { Wrapper, Button } from "../styles";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { getShippingFee } from "../api/order";
import { useContext, useEffect, useState } from "react";
import { DataContext } from "../DataContext";
import { renderSizeAbbreviationHTML } from "../utils";
import { ASSET_URL } from "../constants";

const BottomBar = styled.div`
  padding: 10px 20px !important;
  margin-left: 30px !important;
  margin-right: 30px !important;
  @media only screen and (max-width: 991px) {
    flex-direction: column;
    margin: 10px 20px !important;
  }
`;

const SubmitButton = styled(Button)`
  width: fit-content;
  margin: 30px 30px 30px auto;
  padding: 8px 40px;
  @media only screen and (max-width: 991px) {
    margin: 20px auto;
  }
`;
const ReturnProduct = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 30px 0 30px;
  &:last-child {
    padding-bottom: 0;
  }
  .return-image {
    min-width: 180px;
    min-height: 180px;
    img {
      max-width: 160px;
      max-height: 160px;
    }
    margin-right: 20px;
    @media only screen and (max-width: 991px) {
      min-width: 130px;
      min-height: 130px;
      img {
        max-width: 110px;
        max-height: 110px;
      }
    }
  }
  .return-details {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    flex-grow: 1;
    flex-direction: column;
    & > div {
      display: flex;
      text-align: center;
      line-height: 20px;
      flex-direction: row;
      justify-content: space-between;
      margin: 0;
      width: 100%;
      position: relative;
      span,
      strong {
        margin-bottom: 0;
        line-height: 24px;
        z-index: 1;
        background-color: #fafafa;
      }
      span {
        padding-right: 10px;
      }
      strong {
        padding-left: 10px;
      }
      &::after {
        border: 1px dashed #dddddd;
        content: "";
        top: 50%;
        width: 100%;
        height: 1px;
        right: 0;
        display: block;
        position: absolute;
        z-index: 0;
      }
    }
    @media only screen and (min-width: 992px) {
      .return-details-margin-top {
        margin-top: 10px;
      }
    }
  }
  @media only screen and (max-width: 991px) {
    padding: 20px 20px 0 20px;
    font-size: 14px;
  }
`;

export const ReturnReviewTemplate = ({ products, submit, isFirstReturn, salesTax, customerPostalCode, orderProductCount }) => {
  let totalRefund = 0;
  let totalSalesTaxRefund = 0;

  const salesTaxPerProduct = salesTax / orderProductCount;

  const { settings } = useContext(DataContext);

  const [shippingRate, setShippingRate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const { ground } = await getShippingFee(customerPostalCode, settings.companyZipCode);
      setShippingRate(ground.totalBasePrice);
    };
    fetchData();
  }, []);

  const renderProducts = () => {
    return products.map((product) => {
      if (product.returnType === "Refund") {
        totalRefund = totalRefund + product.productCost + salesTaxPerProduct;
        totalSalesTaxRefund = totalSalesTaxRefund + salesTaxPerProduct;
      }
      if (product.returnType === "Keep") return null;
      return (
        <ReturnProduct key={product.itemId}>
          <div className="return-image product-image border">
            <picture>
              <source srcset={`${ASSET_URL}/designs/small/${product.design}-logo.avif`} type="image/avif" />
              <img src={`${ASSET_URL}/designs/small/${product.design}-logo.gif`} width="180" />
            </picture>
          </div>
          <div className="return-details">
            <div>
              <span>Return Type</span>
              <strong>{product.returnType}</strong>
            </div>
            <div className="return-details-margin-top">
              <span>Original Size</span>
              <strong className="size-badge" dangerouslySetInnerHTML={{ __html: renderSizeAbbreviationHTML(product.size) }} />
            </div>
            <div>
              <span>Original Position</span>
              <strong>{product.position}</strong>
            </div>

            {product.returnType === "Exchange" && (
              <>
                <div className="return-details-margin-top">
                  <span>Exchange Size</span>
                  <strong className="size-badge" dangerouslySetInnerHTML={{ __html: renderSizeAbbreviationHTML(product.sizeExchange) }} />
                </div>
                <div>
                  <span>Exchange Position</span>
                  <strong>{product?.positionExchange}</strong>
                </div>
              </>
            )}
            {product.returnType === "Refund" && (
              <div className="return-details-margin-top">
                <span>Refund Amount</span>
                <strong>${product.productCost}</strong>
              </div>
            )}
          </div>
        </ReturnProduct>
      );
    });
  };

  if (!shippingRate) return null;

  const productsHTML = renderProducts();
  const refundAmount = isFirstReturn ? totalRefund : totalRefund - shippingRate;
  const shippingFee = isFirstReturn ? 0 : shippingRate;
  return (
    <Wrapper className="modal-styles">
      <Modal.Header closeButton>
        <Modal.Title>Review Your Return/Exchange</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>{productsHTML}</div>

        {totalSalesTaxRefund > 0 && (
          <BottomBar className="return-bottom-bar sales-tax">
            <div className="refund-text">
              <span className="text">+ Sales Tax Refund</span>
              <span className="amount">${totalSalesTaxRefund.toFixed(2)}</span>
            </div>
          </BottomBar>
        )}
        {!isFirstReturn && (
          <BottomBar className="return-bottom-bar shipping-fee">
            <div className="refund-text">
              <span className="text"> - Shipping Fee</span>
              <span className="amount">${shippingFee.toFixed(2)}</span>
            </div>
          </BottomBar>
        )}
        {totalRefund > 0 && (
          <BottomBar className="return-bottom-bar">
            <div className="refund-text">
              <span className="text">= Total Refund Amount</span>
              <span className="amount">${refundAmount.toFixed(2)}</span>
            </div>
          </BottomBar>
        )}

        <SubmitButton onClick={() => submit(refundAmount, shippingFee)}>Submit Return</SubmitButton>
      </Modal.Body>
    </Wrapper>
  );
};
